import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { SidebarInset, SidebarProvider } from 'shared-ui';

import { useAuth } from '@/auth/useAuth';
import { useAppConfig } from '@/config/AppConfigContext';
import { ClientProvider } from '@/clients/ClientProvider';
import { useClient } from '@/clients/useClient';
import { clientHasModule, OrbitModule, productKeyFromPathname } from '@/clients/modules';
import { loadLastProduct, resolveLandingRoute } from '@/clients/productPreference';
import { UPSELL_PRODUCTS } from '@/components/upsell/upsellContent';
import { AppLoader } from '@/components/app/AppLoader';
import { useBootstrapLoader } from '@/hooks/useBootstrapLoader';
import { AppHeader } from '@/components/app/AppHeader';
import { AppSidebar } from '@/components/app/AppSidebar';
import { MobileChromeProvider } from '@/components/app/MobileChromeProvider';
import { MobileSidebarTrigger } from '@/components/app/MobileSidebarTrigger';
import { SignOutDialog } from '@/components/signout/SignOutDialog';
import { HelpDialog } from '@/components/help/HelpDialog';
import { UpsellDialog } from '@/components/upsell/UpsellDialog';
import type { ProductKey } from '@/components/upsell/upsellContent';

/** Inner layout — rendered inside ClientProvider so useClient() is available. */
function LayoutInner() {
    const { signOut, user } = useAuth();
    const { selectedClient, clients, canViewNetworkExplorer } = useClient();
    const navigate = useNavigate();
    const [signOutOpen, setSignOutOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    // Product whose upsell overlay is open, or null. Clicking a disabled product in the sidebar
    // opens this dialog without navigating away from the current module.
    const [upsellProduct, setUpsellProduct] = useState<ProductKey | null>(null);

    const findAndFixEnabled = clientHasModule(selectedClient, OrbitModule.LeakDetection);
    const saviEnabled = clientHasModule(selectedClient, OrbitModule.Savi);

    // Active module prefix for the header title, derived from the URL (the source of truth for the
    // current product). null on the bare `/` landing while it redirects — header falls back to the
    // plain section label then.
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const productKey = productKeyFromPathname(pathname);
    // Network Explorer is a view over the Find & Fix data for every client, so its header keeps
    // the Find & Fix breadcrumb (as designed) while the big title names the view, not a client.
    const isExplorerActive = pathname === '/network-explorer' || pathname.startsWith('/network-explorer/');
    const moduleLabel = isExplorerActive
        ? UPSELL_PRODUCTS['find-and-fix'].name.toUpperCase()
        : productKey
          ? UPSELL_PRODUCTS[productKey].name.toUpperCase()
          : undefined;

    return (
        <SidebarProvider
            defaultOpen={false}
            style={
                {
                    '--sidebar-width': '17rem',
                    '--sidebar-width-mobile': 'clamp(14rem, 65vw, 16rem)',
                    '--sidebar-width-icon': '5rem',
                } as CSSProperties
            }
        >
            <AppSidebar
                onSignOutClick={() => {
                    setSignOutOpen(true);
                }}
                onHelpClick={() => {
                    setHelpOpen(true);
                }}
                findAndFixEnabled={findAndFixEnabled}
                saviEnabled={saviEnabled}
                onUpsell={setUpsellProduct}
                userName={user?.name}
                userEmail={user?.email}
            />

            <SidebarInset>
                {/* MobileChromeProvider bridges routes to the shell chrome: it must enclose both
                    the floating trigger (the consumer) and the Outlet (the requesters). */}
                <MobileChromeProvider>
                    <div className="relative flex h-dvh flex-col overflow-hidden">
                        <MobileSidebarTrigger />
                        <AppHeader
                            moduleLabel={moduleLabel}
                            clientName={isExplorerActive ? 'Network Explorer' : selectedClient?.name}
                            clientLogoSrc={isExplorerActive ? null : selectedClient?.logoUrl}
                            explorer={{
                                available: canViewNetworkExplorer,
                                active: isExplorerActive,
                                onSelect: () => {
                                    void navigate({ to: '/network-explorer' });
                                },
                            }}
                            onClientSelected={(clientId) => {
                                // Choosing a client inside the explorer returns to that client's
                                // product; within a product the switch itself is the whole action.
                                if (!isExplorerActive) return;
                                const client = clients.find((c) => c.id === clientId) ?? null;
                                void navigate({ to: resolveLandingRoute(loadLastProduct(), client) });
                            }}
                        />
                        <main className="relative order-1 flex min-h-0 flex-1 flex-col overflow-y-auto p-4 md:order-2">
                            <Outlet />
                        </main>
                        {/*
                         * Generic mobile bottom slot — order-3 places it below AppHeader (order-2)
                         * on small screens. Route layouts that need a persistent bottom bar (e.g.
                         * the stats bar on find-and-fix routes) portal their content here.
                         * Hidden on desktop (md:hidden) because the route layout renders its bar
                         * in normal flow above the map at md+.  (Finding 9)
                         */}
                        <div id="mobile-bottom-slot" className="order-3 shrink-0 md:hidden" />
                    </div>
                </MobileChromeProvider>
            </SidebarInset>

            <SignOutDialog open={signOutOpen} onOpenChange={setSignOutOpen} onConfirm={signOut} />
            <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
            <UpsellDialog
                open={upsellProduct !== null}
                onOpenChange={(open) => {
                    if (!open) setUpsellProduct(null);
                }}
                // Fallback key only matters for the brief close animation frame (open is false then).
                product={upsellProduct ?? 'savi'}
            />
        </SidebarProvider>
    );
}

/** Holds the full app shell behind the loader overlay until bootstrap completes. */
function ClientBootstrapGate({ children }: { children: ReactNode }) {
    const { isLoading } = useClient();
    const { loaderMounted, loaderFading } = useBootstrapLoader(isLoading);

    return (
        <>
            {children}
            {loaderMounted && <AppLoader fading={loaderFading} />}
        </>
    );
}

/**
 * Wraps the app shell with ClientProvider (which configures the axios instance
 * and loads the accessible client list on mount) then renders the layout.
 */
export function RootLayout() {
    const { getAccessToken } = useAuth();
    const config = useAppConfig();
    // Deep-link client from the URL `?client=` param. Read here (inside the router) and passed
    // into ClientProvider so the active client is resolved from the URL *before* the route's
    // ProductGuard evaluates module access — keeping ClientProvider itself router-free.
    const urlClientId = useRouterState({
        select: (s) => (s.location.search as { client?: string }).client,
    });

    return (
        <ClientProvider apiBaseUrl={config.apiBaseUrl} getAccessToken={getAccessToken} initialClientId={urlClientId}>
            <ClientBootstrapGate>
                <LayoutInner />
            </ClientBootstrapGate>
        </ClientProvider>
    );
}
